import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { LoadingPage } from "./components/StudioShell";
import { ServiceNav } from "./components/TeacherChrome";
import { TeacherDemoBar } from "./components/TeacherDemoBar";
import { getMe, SESSION_LOST_MESSAGE, sessionLostEvent } from "./lib/api";
import { navigate, navigationEvent } from "./lib/navigation";
import { clearSession, currentWorkId, loadLatestSession, loadSession, pathForStep, updateSession } from "./lib/session";
import { chooseResumeStep } from "./lib/resumeStep";
import { RESUME_RETRY_DELAYS_MS, resumeFailureAction } from "./lib/resumeFailure";
import { DonePage } from "./pages/DonePage";
import { JoinPage } from "./pages/JoinPage";
import { LandingPage } from "./pages/LandingPage";
import { LyricsPage } from "./pages/LyricsPage";
import { ModePage } from "./pages/ModePage";
import { NamePage } from "./pages/NamePage";
import { ResultPage } from "./pages/ResultPage";
import { StylePage } from "./pages/StylePage";
import { HummingPage } from "./pages/HummingPage";
import { TeacherDashPage } from "./pages/TeacherDashPage";
import { TeacherLivePage } from "./pages/TeacherLivePage";
import { PartyPage } from "./pages/PartyPage";
import { SongSourcePage } from "./pages/SongSourcePage";
import { StoryboardPage } from "./pages/StoryboardPage";
import { CutsPage } from "./pages/CutsPage";
import { MvDonePage } from "./pages/MvDonePage";
import { EditPage } from "./pages/EditPage";
import { FinalPage } from "./pages/FinalPage";
import { ArScanPage } from "./pages/ArScanPage";
import { PosterScanPage, posterManifestUrl } from "./pages/PosterScanPage";
import { TeacherArPage } from "./pages/TeacherArPage";
import { PicturePickPage } from "./pages/PicturePickPage";
import { PictureImagePage } from "./pages/PictureImagePage";
import { PictureDonePage } from "./pages/PictureDonePage";
import { PosterDrawPage } from "./pages/poster/PosterDrawPage";
import { PosterSongArPage } from "./pages/poster/PosterSongArPage";
import { PosterConfirmPage } from "./pages/poster/PosterConfirmPage";
import { PosterEditPage } from "./pages/poster/PosterEditPage";

export interface RouteDefinition {
  suffix: "" | "/lyrics" | "/humming" | "/style" | "/result" | "/done";
  component: ComponentType<{ workId: string }>;
}

export const routes: readonly RouteDefinition[] = [
  { suffix: "", component: ModePage },
  { suffix: "/lyrics", component: LyricsPage },
  { suffix: "/humming", component: HummingPage },
  { suffix: "/style", component: StylePage },
  { suffix: "/result", component: ResultPage },
  { suffix: "/done", component: DonePage },
];

interface MvRouteDefinition {
  suffix: "" | "/story" | "/cuts" | "/done" | "/edit" | "/final";
  component: ComponentType<{ workId: string }>;
}

export const mvRoutes: readonly MvRouteDefinition[] = [
  { suffix: "", component: SongSourcePage },
  { suffix: "/story", component: StoryboardPage },
  { suffix: "/cuts", component: CutsPage },
  { suffix: "/done", component: MvDonePage },
  { suffix: "/edit", component: EditPage },
  { suffix: "/final", component: FinalPage },
];

interface PictureRouteDefinition {
  suffix: "" | "/image" | "/song" | "/done";
  component: ComponentType<{ workId: string }>;
}

function PictureSongPage({ workId }: { workId: string }) {
  return <SongSourcePage workId={workId} variant="picture" />;
}

/** 그림 수업(2026-09-24) — 서버 단계 `pic_pick·pic_image·pic_song·pic_done` 과 1:1 */
export const pictureRoutes: readonly PictureRouteDefinition[] = [
  { suffix: "", component: PicturePickPage },
  { suffix: "/image", component: PictureImagePage },
  { suffix: "/song", component: PictureSongPage },
  { suffix: "/done", component: PictureDonePage },
];

interface PosterRouteDefinition {
  suffix: "" | "/edit" | "/song" | "/confirm";
  component: ComponentType<{ workId: string }>;
}

/** 포스터 수업(2026-10-01) — 서버 단계 `pst_draw·pst_edit·pst_song·pst_done` 과 1:1 */
export const posterRoutes: readonly PosterRouteDefinition[] = [
  { suffix: "", component: PosterDrawPage },
  { suffix: "/edit", component: PosterEditPage },
  { suffix: "/song", component: PosterSongArPage },
  { suffix: "/confirm", component: PosterConfirmPage },
];

function currentLocation(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function EndedPage({ onReset }: { onReset: () => void }) {
  return (
    <main className="join-page">
      <section className="join-card join-complete">
        <div className="person-icon" aria-hidden="true">♪</div>
        <h1 tabIndex={-1}>수업이 끝났어요</h1>
        <p>완성한 노래는 선생님 화면에 안전하게 남아 있어요.</p>
        <button className="button-primary join-primary" type="button" onClick={onReset}>
          다른 수업 참여하기
        </button>
      </section>
    </main>
  );
}

/**
 * 참여가 풀렸을 때(B3) — 「수업이 끝났어요」와 **다른 화면**이어야 한다.
 * 수업은 살아 있고 학생만 튕긴 것이라, 할 일이 「끝났으니 나가기」가 아니라 「코드 다시 넣기」다.
 */
function LostPage({ onRejoin }: { onRejoin: () => void }) {
  return (
    <main className="join-page">
      <section className="join-card">
        <div className="person-icon" aria-hidden="true">♪</div>
        <h1 tabIndex={-1}>다시 참여해 주세요</h1>
        <p>{SESSION_LOST_MESSAGE}</p>
        <button className="button-primary join-primary" type="button" onClick={onRejoin}>
          수업 코드 넣기
        </button>
      </section>
    </main>
  );
}

/**
 * 이어하기 조회가 잠깐 실패했을 때(부검 2026-10-26) — 접속 정보는 지우지 않았다.
 * 할 일은 「다시 시도」이고, 그래도 안 되면 코드를 새로 넣는 길도 열어 둔다.
 */
function ResumeRetryPage({ onRetry, onRejoin }: { onRetry: () => void; onRejoin: () => void }) {
  return (
    <main className="join-page">
      <section className="join-card">
        <div className="person-icon" aria-hidden="true">♪</div>
        <h1 tabIndex={-1}>연결이 잠깐 끊겼어요</h1>
        <p>하던 작업은 그대로 남아 있어요. 잠시 뒤에 다시 시도해 주세요.</p>
        <button className="button-primary join-primary" type="button" onClick={onRetry}>
          다시 시도
        </button>
        <button className="button-secondary join-primary" type="button" onClick={onRejoin}>
          수업 코드 다시 넣기
        </button>
      </section>
    </main>
  );
}

/**
 * `/` = 랜딩(한결 §9.F), `/join` = 학생 코드 입력.
 * 세션 복원 대상 경로 — 진행 중인 수업이 있으면 이 두 경로에서만 이어보기로 넘긴다.
 */
export function isSessionEntryPath(pathname: string): boolean {
  return pathname === "/" || pathname === "/join";
}

/** 랜딩 경로 여부(테스트용 순수 함수 — DOM 비의존). */
export function isLandingPath(pathname: string): boolean {
  return pathname === "/";
}

const SESSION_CODE_PATTERN = /^\d{6}$/u;

/**
 * QR·공유 링크는 `/?code=123456` 으로 들어온다 — 랜딩 대신 참여 흐름을 그린다.
 *
 * 🚨 리다이렉트(navigate)로 처리하면 안 된다: 자식 effect 가 부모(Router)의 `navigationEvent`
 * 리스너 등록보다 먼저 실행돼 첫 진입 이벤트가 유실된다(실측). 라우팅 단계에서 결정한다.
 */
export function showsJoinAtRoot(pathname: string, search: string): boolean {
  if (!isLandingPath(pathname)) return false;
  const code = new URLSearchParams(search).get("code");
  return code !== null && SESSION_CODE_PATTERN.test(code);
}

function resolveRoute(location: string): ReactNode {
  const url = new URL(location, window.location.origin);
  if (showsJoinAtRoot(url.pathname, url.search)) return <JoinPage />;
  if (isLandingPath(url.pathname)) return <LandingPage />;
  if (url.pathname === "/join") return <JoinPage />;
  if (url.pathname === "/join/name") return <NamePage />;
  if (url.pathname === "/teacher") return <TeacherDashPage />;
  // 키링 QR 이 가리키는 주소. **인쇄물에 박히므로 절대 바꾸지 않는다**(server/src/ar/keys.ts).
  const keyring = url.pathname.match(/^\/k\/([A-Za-z2-7]{8})$/u);
  if (keyring) return <ArScanPage shortId={(keyring[1] ?? "").toUpperCase()} />;
  // 포스터 AR — 포스터 QR 이 가리키는 그 학생 포스터(작품 id). 인쇄물에 박히므로 바꾸지 않는다(키링 /k/ 와 따로).
  const posterAr = url.pathname.match(/^\/p\/([0-9a-fA-F-]{36})$/u);
  if (posterAr) return <PosterScanPage manifestUrl={posterManifestUrl("p", posterAr[1] ?? "")} />;
  // 반 QR — 새 포스터 수업의 학생 포스터 QR 이 가리키는 그 반 전체 화면(수업 id). 인쇄물에 박히므로 바꾸지 않는다.
  const classAr = url.pathname.match(/^\/c\/([0-9a-fA-F-]{36})$/u);
  if (classAr) return <PosterScanPage manifestUrl={posterManifestUrl("c", classAr[1] ?? "")} />;
  const teacherAr = url.pathname.match(/^\/teacher\/ar\/([^/]+)$/u);
  if (teacherAr) return <TeacherArPage sessionId={decodeURIComponent(teacherAr[1] ?? "")} />;
  const teacherLive = url.pathname.match(/^\/teacher\/live\/([^/]+)$/u);
  if (teacherLive) return <TeacherLivePage sessionId={decodeURIComponent(teacherLive[1] ?? "")} />;
  const teacherParty = url.pathname.match(/^\/teacher\/party\/([^/]+)$/u);
  if (teacherParty) return <PartyPage sessionId={decodeURIComponent(teacherParty[1] ?? "")} />;
  const match = url.pathname.match(/^\/music\/w\/([^/]+)(\/lyrics|\/humming|\/style|\/result|\/done)?$/u);
  if (match) {
    const workId = decodeURIComponent(match[1] ?? "");
    const suffix = (match[2] ?? "") as RouteDefinition["suffix"];
    const route = routes.find((candidate) => candidate.suffix === suffix);
    if (!route) return <LandingPage />;
    const Page = route.component;
    return <Page workId={workId} />;
  }
  const picMatch = url.pathname.match(/^\/pic\/w\/([^/]+)(\/image|\/song|\/done)?$/u);
  if (picMatch) {
    const workId = decodeURIComponent(picMatch[1] ?? "");
    const suffix = (picMatch[2] ?? "") as PictureRouteDefinition["suffix"];
    const route = pictureRoutes.find((candidate) => candidate.suffix === suffix);
    if (!route) return <LandingPage />;
    const Page = route.component;
    return <Page workId={workId} />;
  }
  const posterMatch = url.pathname.match(/^\/poster\/w\/([^/]+)(\/edit|\/song|\/confirm)?$/u);
  if (posterMatch) {
    const workId = decodeURIComponent(posterMatch[1] ?? "");
    const suffix = (posterMatch[2] ?? "") as PosterRouteDefinition["suffix"];
    const route = posterRoutes.find((candidate) => candidate.suffix === suffix);
    if (!route) return <LandingPage />;
    const Page = route.component;
    return <Page workId={workId} />;
  }
  const mvMatch = url.pathname.match(/^\/mv\/w\/([^/]+)(\/story|\/cuts|\/done|\/edit|\/final)?$/u);
  if (!mvMatch) return <LandingPage />;
  const workId = decodeURIComponent(mvMatch[1] ?? "");
  const suffix = (mvMatch[2] ?? "") as MvRouteDefinition["suffix"];
  const route = mvRoutes.find((candidate) => candidate.suffix === suffix);
  if (!route) return <LandingPage />;
  const Page = route.component;
  return <Page workId={workId} />;
}

/**
 * 공용 네비를 숨길 경로(표준 `hidden` = 학생 QR 경로).
 * 랜딩·교사 화면에서는 보여야 한다 — 2026-07-29 실브라우저 QA 에서 교사 화면에만 있던 것을 교정.
 */
export function hidesServiceNav(pathname: string, search: string): boolean {
  return pathname.startsWith("/join")
    || pathname.startsWith("/music/w/")
    || pathname.startsWith("/mv/w/")
    || pathname.startsWith("/pic/w/")
    || pathname.startsWith("/poster/w/")
    // 🚨 키링 스캔은 카메라가 화면 전체를 덮는다. 그 위에 네비가 얹히면 «비추는 곳» 을 가린다.
    || pathname.startsWith("/k/")
    || pathname.startsWith("/p/")
    || pathname.startsWith("/c/")
    || showsJoinAtRoot(pathname, search);
}

/**
 * 학생 작업 흐름 경로인가 — 시연작 교사 바를 얹을 자리(§9.H-14 상위집합).
 * 공용 네비가 숨겨지는 경로와 같은 자리라, 교사에겐 그 자리에 교사 바가 대신 들어간다.
 */
export function isStudentFlowPath(pathname: string): boolean {
  return pathname.startsWith("/music/w/") || pathname.startsWith("/mv/w/") || pathname.startsWith("/pic/w/")
    || pathname.startsWith("/poster/w/");
}

export function Router(): ReactNode {
  const [location, setLocation] = useState(currentLocation);
  const [checkingSession, setCheckingSession] = useState(false);
  const [ended, setEnded] = useState(false);
  const [lost, setLost] = useState(false);
  // 이어하기 조회가 자동 재시도까지 실패했다 — 토큰은 지우지 않고 「다시 시도」를 보인다.
  const [resumeFailed, setResumeFailed] = useState(false);
  const [resumeAttempt, setResumeAttempt] = useState(0);
  // 「수업 코드 다시 넣기」 직후 한 번은 자동 이어하기를 건너뛴다 — 안 그러면 남은 다른 작품이 곧바로 열린다(Codex 검수).
  const skipResumeOnce = useRef(false);

  useEffect(() => {
    const update = (): void => setLocation(currentLocation());
    const showEnded = (): void => setEnded(true);
    const showLost = (): void => setLost(true);
    window.addEventListener("popstate", update);
    window.addEventListener(navigationEvent, update);
    window.addEventListener("mvs:session-ended", showEnded);
    window.addEventListener(sessionLostEvent, showLost);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener(navigationEvent, update);
      window.removeEventListener("mvs:session-ended", showEnded);
      window.removeEventListener(sessionLostEvent, showLost);
    };
  }, []);

  useEffect(() => {
    const url = new URL(location, window.location.origin);
    // 🔑 진입 화면에서만, 그리고 **가장 최근에 저장된** 작품으로 이어 간다(session.ts loadLatestSession).
    const skip = skipResumeOnce.current;
    skipResumeOnce.current = false;
    // 위치가 바뀌면(코드 링크로 이동 등) 지난 재시도 화면을 내린다.
    setResumeFailed(false);
    const resumable = isSessionEntryPath(url.pathname) && !url.searchParams.has("code") && !skip;
    const session = resumable ? loadLatestSession() : null;
    // 참여 화면으로 돌아왔으면 「참여가 풀렸다」 표시를 내린다.
    // 안 내리면 다시 참여한 뒤 작업 화면으로 갈 때 그 화면이 또 뜬다.
    if (isSessionEntryPath(url.pathname)) setLost(false);
    if (!session) {
      setCheckingSession(false);
      return;
    }
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    setCheckingSession(true);
    const attempt = (tries: number): void => {
      void getMe()
        .then((me) => {
          if (!active) return;
          setCheckingSession(false);
          if (me.session_status === "ended" && me.active_job === null) {
            setEnded(true);
            return;
          }
          updateSession({
            kind: me.session_kind,
            activeJobId: me.active_job?.id ?? null,
          }, session.workId);
          // 입장 화면(NamePage)과 같은 규칙 — 그 수업의 단계가 아니면 첫 단계, 곡이 만들어지는 중이면 결과.
          navigate(pathForStep(me.work_id, chooseResumeStep(me, me.work_id, me.session_kind)), { replace: true });
        })
        .catch((error: unknown) => {
          if (!active) return;
          const action = resumeFailureAction(error);
          const status = typeof error === "object" && error && "status" in error ? String(error.status) : "없음";
          const name = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
          if (action === "retry" && tries < RESUME_RETRY_DELAYS_MS.length) {
            retryTimer = setTimeout(() => attempt(tries + 1), RESUME_RETRY_DELAYS_MS[tries]);
            return;
          }
          setCheckingSession(false);
          if (action === "ended") {
            setEnded(true);
          } else if (action === "clear") {
            // 토큰이 정말 무효일 때(401·403·404)만 지운다.
            // 🔑 그 사이 다른 탭에서 다시 참여해 새 토큰이 저장됐으면 그건 지우지 않는다(Codex 검수).
            if (loadSession(session.workId)?.token === session.token) {
              clearSession("me-failed", `진입 화면 자동 이어하기에서 /api/me 실패 — status=${status} · ${name}`, session.workId);
            }
          } else {
            // 🔑 5xx·네트워크 오류는 토큰을 지우지 않는다(부검 2026-10-26). 무엇이 실패했는지만 남긴다.
            console.warn(`[mvs:resume-retry] /api/me 실패 ${tries + 1}회 — status=${status} · ${name}`);
            setResumeFailed(true);
          }
        });
    };
    attempt(0);
    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [location, resumeAttempt]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    const frame = window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>("h1")?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location, ended]);

  const page = useMemo(() => resolveRoute(location), [location]);
  const navUrl = new URL(location, window.location.origin);
  // 시연작(§9.H-14 B형): 교사가 자기 데모 세션에 참여자로 들어와 있으면 학생 화면 위에 교사 바를 얹는다.
  // 학생에겐 `demo` 가 false 라 아무것도 달라지지 않는다 — 학생 화면 ⊂ 교사 화면.
  // 🚨 «이 탭의» 작품만 본다. 가장 최근 칸으로 떨어지면 다른 탭의 시연 상태가 학생 화면 위에 교사 바로 샌다.
  const tabWorkId = currentWorkId();
  const demoSession = tabWorkId ? loadSession(tabWorkId) : null;
  const demoBar = demoSession?.demo && isStudentFlowPath(navUrl.pathname)
    ? <TeacherDemoBar session={demoSession} />
    : null;
  // 공용 네비는 앱 최상단에 한 번만. 화면별로 붙이면 랜딩에서 빠진다(2026-07-29 QA).
  const shell = (content: ReactNode): ReactNode => (
    <>
      <ServiceNav hidden={hidesServiceNav(navUrl.pathname, navUrl.search)} />
      {demoBar}
      {content}
    </>
  );
  if (checkingSession) return shell(<LoadingPage label="진행하던 수업을 불러오는 중..." />);
  if (resumeFailed && isSessionEntryPath(navUrl.pathname) && !navUrl.searchParams.has("code")) {
    return shell(
      <ResumeRetryPage
        onRetry={() => setResumeAttempt((value) => value + 1)}
        onRejoin={() => {
          const latest = loadLatestSession();
          clearSession("rejoin", undefined, latest?.workId);
          setResumeFailed(false);
          skipResumeOnce.current = true;
          navigate("/join", { replace: true });
        }}
      />,
    );
  }
  if (ended && !navUrl.pathname.startsWith("/teacher")) {
    return shell(
      <EndedPage
        onReset={() => {
          clearSession("ended");
          setEnded(false);
          navigate("/join", { replace: true });
        }}
      />,
    );
  }
  // 참여가 풀린 것은 「끝남」보다 약한 상태다 — 끝남이 이미 떠 있으면 그쪽을 유지한다.
  // 이미 참여 화면(랜딩·/join)에 있으면 새 화면을 끼우지 않는다. 거기서 할 일이 곧 코드 넣기다.
  if (lost && !navUrl.pathname.startsWith("/teacher") && !isSessionEntryPath(navUrl.pathname)) {
    return shell(
      <LostPage
        onRejoin={() => {
          clearSession("rejoin");
          setLost(false);
          navigate("/join", { replace: true });
        }}
      />,
    );
  }
  return shell(page);
}
